import React, { useState, useEffect } from 'react';
import { Modal, Panel, TabSlide, TabSlider } from '@keplerco/core';
import { SkillScoresRequest, SkillScoresSkillCategoryResponse } from '../../../models/overmind/analytics';
import { getSunBurstData } from '../../../pages/your-skills/your-skills.helpers';
import { SkillLevelsExplainerPanelLayout } from '../../layouts/focus-panel-layouts/skill-levels-explainer-panel/skill-levels-explainer-panel.layout';
import { useAppActions, useAppState } from '../../../overmind';
import { ResponseGroup } from '../../../enums/analytics';
import { EmptyState } from '../../../components/general/empty-state/empty-state';
import classNames from 'classnames';
import styles from './sunburst.module.css';
import { SunburstModalLayout } from './sunburst-modal/sunburst-modal.layout';
import { ISunburstWidgetProps } from './sunburst.models';
import { SkeletonOverlay } from '../../../components/general/loading-state/skeleton-overlay';
import { SkillScoresSearchParams } from '../../../models/overmind/search-params';
import { SunburstLayout } from './sunburst.layout';

export function SunburstWidget({ chartTitle, skillScoreTitle, skillScoreUseRAGColours, permissionSearchParams, assessmentSearchParams, scoreComparison }: ISunburstWidgetProps) {
  const actions = useAppActions();
  const { companyVariables, dateRange } = useAppState();

  const [isLoading, setIsLoading] = useState(true);
  const [skillCategories, setSkillCategories] = useState<SkillScoresSkillCategoryResponse[]>();
  const [selectedSkillCategory, setSelectedSkillCategory] = useState<SkillScoresSkillCategoryResponse | undefined>(undefined);
  const [isLevelsModalOpen, setIsLevelsModalOpen] = useState(false);

  useEffect(() => {
    async function getData() {
      setIsLoading(true);

      const searchParams: SkillScoresSearchParams = {
        ...permissionSearchParams,
        ...assessmentSearchParams,
        startDate: dateRange?.from?.toJSON(),
        endDate: dateRange?.to?.toJSON(),
        skillSlug: undefined,
        skillSubTypeSlug: undefined,
        scoreComparison,
        includeLearnerCount: false,
      };
      const request: SkillScoresRequest = {
        responseGroup: ResponseGroup.Skill,
        searchParams,
      };
      const response = await actions.analyticsGetSkillScores(request);

      const nextSkillCategories: SkillScoresSkillCategoryResponse[] = [];
      if (!!response && !!response[0] && !!response[0].skillCategories) {
        response[0].skillCategories.forEach(skillCategory => {
          if (!skillCategory.skills?.length) return;
          
          if (!skillCategory.name) {
            skillCategory.name = 'Uncategorised skills';
          }
          
          nextSkillCategories.push(skillCategory);
        });
      }
      setSkillCategories(nextSkillCategories);

      setIsLoading(false);
    }

    getData();
  }, [permissionSearchParams, assessmentSearchParams, dateRange]);

  return (
    <SkeletonOverlay isLoading={isLoading} width="100%" height="100%">
      <div className={styles.skillsReportContainer}>
        {!skillCategories?.length ? (
          <div className={classNames(styles.skillsReportCard, 'card', styles.emptyState)}>
            <EmptyState />
          </div>
        ) : skillCategories?.length > 1 ? (
          <TabSlider>
            {skillCategories.map((skillType, index, array) => {
              const isFirst = index === 0;
              const isLast = index === array.length - 1;
              const sunBurstData = getSunBurstData(skillType.skills);

              return (
                <TabSlide name={skillType.name} key={skillType.slug}>
                  <SunburstLayout
                    skillType={skillType}
                    sunBurstData={sunBurstData}
                    isFirst={isFirst}
                    isLast={isLast}
                    skillScoreUseRAGColours={skillScoreUseRAGColours}
                    chartTitle={chartTitle}
                    skillScoreTitle={skillScoreTitle}
                    setSelectedSkillSubType={setSelectedSkillCategory}
                    scoreComparison={scoreComparison}
                  />
                </TabSlide>
              );
            })}
          </TabSlider>
        ) : (
          <>
            {skillCategories.length > 0 && (
              <SunburstLayout
                key={skillCategories[0].slug}
                skillType={skillCategories[0]}
                sunBurstData={getSunBurstData(skillCategories[0].skills)}
                skillScoreUseRAGColours={skillScoreUseRAGColours}
                chartTitle={chartTitle}
                skillScoreTitle={skillScoreTitle}
                setSelectedSkillSubType={setSelectedSkillCategory}
                scoreComparison={scoreComparison}
              />
            )}
          </>
        )}
      </div>

      {companyVariables.useLevels && (
        <Panel open={isLevelsModalOpen} onClose={() => setIsLevelsModalOpen(false)}>
          <SkillLevelsExplainerPanelLayout onClose={() => setIsLevelsModalOpen(false)} />
        </Panel>
      )}

      {!!selectedSkillCategory && (
        <Modal size="extralarge" open={!!selectedSkillCategory} onClose={() => setSelectedSkillCategory(undefined)}>
          <SunburstModalLayout skillSubType={selectedSkillCategory} skillScoreTitle={skillScoreTitle} skillScoreUseRAGColours={skillScoreUseRAGColours} scoreComparison={scoreComparison} />
        </Modal>
      )}
    </SkeletonOverlay>
  );
}
